// Core
import React, { useEffect, useCallback, useState } from 'react';
import { Box, Button, Flex, Grid, Text, Image, useDisclosure, useBreakpointValue, useColorMode, useColorModeValue} from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';
import { useQuery } from 'react-query';

// Assets
import crystal from 'assets/img/details/crystal-3.png';
import { apiService } from 'services/ApiService';
import { ReactComponent as PaymentMethod } from 'assets/svg/payment-method.svg';

// Components
import Card from 'components/Card/Card';
import Warning from 'components/Form/Warning';
import SuccessCard from 'components/Form/Success';
import PrivilegePlan from './components/PrivilegePlan';
import Prizes from './components/Prizes';
import UpgradeDowngrade from './components/UpgradeDowngrade';
import MoreOpportunities from './components/MoreOpportunities';
import Discount from './components/Discount';
import ActivePlan from './components/ActivePlan';
import ActivePlans from './components/ActivePlans';
import CancelSubscriptionModal from './components/CancelSubscription';
import { getLinkLoyaltyMembership } from 'services/Utils';

const overdueInvoiceFallback = { hasOverdue: false, invoiceUrl: null };

const Membership = (props) => {
  const { userData } = props;

  const [successCard, setSuccessCard] = useState();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const successParam = params.get('success');
    if (successParam) {
      setSuccessCard(successParam);
    }

    const cancelParam = params.get('cancel');
    if (cancelParam) {
      onOpen();
    }
  }, []);

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const heightInActiveBlock = useBreakpointValue({ base: '430px', sm: '400px' });
  const colorTextPaymentChange = useColorModeValue('#7787B0', '#8794C2');

  const { isOpen, onClose, onOpen } = useDisclosure();

  const [isLoadingCustomerPortal, setIsLoadingCustomerPortal] = useState(false);
  const goToCustomerPortal = useCallback(async () => {
    setIsLoadingCustomerPortal(true);
    const response = await apiService.goToCustomerPortal();

    if (response.message === 'Success') {
      window.location.href = response.data;
    } 

    setIsLoadingCustomerPortal(false);
  }, []);

  const handleCancelMembership = async () => {
    onOpen();
  };

  /*const handleCancelMembership = async () => {
    try {
      await apiService.unSubscription({
        id: userData?.activeSubscription?.id
      });
    } catch (error) {
      console.error('Error:', error);
    } 

    window.location.reload();
  };*/

  const { data: activePlans, isLoading } = useQuery({
      queryKey: ['active-plans'],
      queryFn: async () => {
          const response = await apiService.getSubscriptions({status: 'active'});
          return response.data;
      },
      refetchOnWindowFocus: false
  });

  const { data: pausedPlans } = useQuery({
      queryKey: ['paused-plans'],
      queryFn: async () => {
          const response = await apiService.getSubscriptions({status: 'paused'});
          return response.data;
      },
      refetchOnWindowFocus: false
  });

  const { data: overdueInvoice } = useQuery({
      queryKey: ['overdue-invoice'],
      queryFn: async () => {
          if(!localStorage.getItem('auth')) { return overdueInvoiceFallback; }

          const response = await apiService.getOverdueInvoice();
          return response?.data ?? overdueInvoiceFallback;
      },
      refetchOnWindowFocus: true
  });
  const overdueData = overdueInvoice ?? overdueInvoiceFallback;
  const hasOverdue = overdueData.hasOverdue;
  const [isOverduePaymentLoading, setIsOverduePaymentLoading] = useState(false);
  const goToOverduePayment = useCallback(async () => {
    setIsOverduePaymentLoading(true);

    if (overdueData.invoiceUrl) {
      window.location.href = overdueData.invoiceUrl;
    } else {
      const response = await apiService.goToCustomerPortal();

      if (response?.message === 'Success') {
        window.location.href = response.data;
      }
    }

    setIsOverduePaymentLoading(false);
  }, [overdueData]);

  const goToUpdatePaymentPortal = useCallback(async (id) => {
    setIsLoadingCustomerPortal(true);
    const response = await apiService.goToSubscriptionCustomerPortal({id});

    if (response.message === 'Success') {
      window.location.href = response.data;
    } 

    setIsLoadingCustomerPortal(false);
  }, []);

  const handleBeforeUnSubscription = async () => {
    try {
      await apiService.beforeUnSubscription({
        subscriptionId: userData?.activeSubscription?.id
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const confirmHandleCancelMembership = async ({reason}) => {
    try {
      await apiService.unSubscriptionWithReason({
        id: userData?.activeSubscription?.id,
        reason
      });

      /*if(activePlans?.length === 1){
        props.handleLogout();
      }*/
    } catch (error) {
      console.error('Error:', error);
    } 

    window.location.reload();
  };

  const { data: petrolSummary } = useQuery({
      queryKey: ['petrol-summary'],
      queryFn: async () => {
          const response = await apiService.getPetrolSummary();
          return response.data;
      },
      enabled: userData?.activePlan?.type === 'fuel',
      refetchOnWindowFocus: true,
      retry: false
  });

  return (
    <>
      <Flex align='left' mb={{ base: '10px', md: '16px' }}
        direction={{ base: 'row' }}
        w={{ sm: '100%' }}
        textAlign={{ base: 'center', md: 'start' }}
        borderBottom="1px"
        className="membership-tabs">
        <Link to="/account/membership" className="membership-tab-active">Active Plan</Link>
        <Link to="/account/membership/subscriptions?status=active">Manage Subscriptions</Link>
      </Flex>

      { successCard &&
         <SuccessCard success={
          successCard === 'downgrade' ? 'Your membership has been adjusted to a lower tier. You continue to have access to all giveaways and member discounts.'
          : successCard === 'paused' ? 'Your subscription has been paused for 30 days and will automatically resume after this period. All entries remain saved.'
          : 'Success'
        } maxW="100%" />
      }

      { !userData?.activePlan && pausedPlans?.length > 0 && (
        <Warning 
          text="You have paused subscriptions"
          link="/account/membership/subscriptions?status=paused" />
      )}

      { userData?.activePlan && userData?.activePlan?.type !== 'fuel' && activePlans?.length === 1 &&
        <Grid bg={ isDark ? '#0a1027' : 'white' } p="12px"
          borderRadius="16px"
          mb={{ base: '12px', md: '16px', lg: '18px'}}
          alignItems="center"
          border={ isDark ? '1px solid #262D47' : '1px solid #CBE7FF'}
          templateColumns={{ sm: '1fr', md: '1fr 0.5fr' }} gap="24px" templateRows='1fr'>
          <Box>
            <Flex gap="8px" alignItems="center">
              <Box 
                p="4px"
                borderRadius="8px"
                w="32px"
                h="32px"
                background="rgba(9, 155, 255, 0.10)"
                >
                <PaymentMethod />
              </Box>

              <Text variant="title-lg" fontSize="20px">Payment Method</Text>
            </Flex>

            <Text fontSize="15px" color={colorTextPaymentChange} mt="8px">
              Just update your payment method — you can change it anytime
            </Text>
          </Box>

          <Box>
            <Button 
              onClick={() => goToUpdatePaymentPortal(activePlans[0]?.id)}
              isLoading={isLoadingCustomerPortal}
              variant={ isDark ? 'button-md-g' : 'button-md'}>
              Update payment method
            </Button>
          </Box>
        </Grid>
      }

      <Box position="relative" 
        h={!userData?.activePlan ? heightInActiveBlock : 'auto'} 
        overflow="hidden" borderRadius="16px 16px">
      
        { activePlans?.length === 1 ? 
          <ActivePlan 
            userData={userData} 
            handleCancelMembership={handleCancelMembership} 
            goToCustomerPortal={goToCustomerPortal}
            isLoadingCustomerPortal={isLoadingCustomerPortal} />
          : 
          <ActivePlans userData={userData} activePlans={activePlans} />
        }

        { activePlans?.length === 1 && 
          <PrivilegePlan userData={userData} countActivePlans={activePlans?.length} petrolSummary={petrolSummary} />
        }

        { userData?.activePlan && (<Prizes userData={userData} countActivePlans={activePlans?.length} petrolEntriesAmount={petrolSummary?.stats?.entriesAmount || 0}  />)}

        { hasOverdue && !userData?.activePlan && (
          <Box
            borderRadius="16px"
            w="100%"
            h="100%"
            py="80px"
            zIndex="9"
            top="0"
            left="0"
            overflow="hidden"
            position="absolute"
            className="animation-account-membership"
            bg={ isDark ? 'rgba(0, 0, 0, 0.40)' : 'rgba(38, 70, 166, 0.30)'}
            backdropFilter="blur(6px)">
            <Box m="0 auto" maxW="398px" px="30px" textAlign="center" zIndex="10" position="relative">
              <Text variant="header" fontSize={{base: '28px'}} color="white">Overdue Payment</Text>
              <Text color="white" mt="5px">
                You have an unpaid invoice for your LMCT+ subscription. Please update your payment to restore your benefits.
              </Text>
            </Box>

            <Box p={{base: '20px', md: '0px'}} m="28px auto 0 auto" maxW="444px" zIndex="9" position="relative">
              <Button
                isLoading={isOverduePaymentLoading}
                onClick={goToOverduePayment}
                variant="button-lg-g">
                Update Payment
              </Button>
            </Box>

            <Box position="absolute" bottom="-25px" w="100%"
              left={{base: '-20%', sm: '-10%', lg: '0'}}
              transform={{base: 'scale(0.6)', sm: 'scale(0.8)', lg: 'scale(1)'}}>
              <Image className="animation-account-membership-crystal1" src={crystal} alt="crystal" position="absolute" left="103px" bottom="-10px" transform="rotate(-11deg) scaleX(-1)" width="150px" />
              <Image className="animation-account-membership-crystal2" src={crystal} alt="crystal" position="absolute" left="-115px" bottom="-22px" transform="rotate(18deg) scaleX(-1)" width="340px" />
              <Image className="animation-account-membership-crystal3" src={crystal} alt="crystal" position="absolute" left="-6px" bottom="-25px" transform="rotate(4deg) scaleX(-1)" width="200px" zIndex="8" />
            </Box>

            <Box position="absolute" bottom="-25px" w="100%"
              right={{base: '-20%', sm: '-10%', lg: '0'}}
              transform={{base: 'scale(0.6) scaleX(-1)', sm: 'scale(0.8) scaleX(-1)', lg: 'scale(1) scaleX(-1)'}}>
              <Image className="animation-account-membership-crystal4" src={crystal} alt="crystal" position="absolute" left="103px" bottom="-2px" transform="rotate(11deg) scaleX(-1)" width="140px" />
              <Image className="animation-account-membership-crystal5" src={crystal} alt="crystal" position="absolute" left="-115px" bottom="-12px" transform="rotate(18deg) scaleX(-1)" width="330px" />
              <Image className="animation-account-membership-crystal6" src={crystal} alt="crystal" position="absolute" left="-6px" bottom="-15px" transform="rotate(-40deg) scaleX(-1)" width="200px" zIndex="8" />
            </Box>
          </Box>
        )}

        { !userData?.activePlan && !hasOverdue && (
          <Box
            borderRadius="16px"
            w="100%"
            h="100%"
            py="80px"
            zIndex="9"
            top="0"
            left="0"
            overflow="hidden"
            position="absolute"
            className="animation-account-membership"
            bg={ isDark ? 'rgba(0, 0, 0, 0.40)' : 'rgba(38, 70, 166, 0.30)'}
            backdropFilter="blur(6px)">
            <Box m="0 auto" maxW="398px" px="30px" textAlign="center" zIndex="10" position="relative">
              <Box m="0 auto 5px auto"
                 borderRadius={{base: '8px'}}
                 p="3px 7px"
                 w="183px"
                 background="white">
                <Text variant="header-gradient" fontSize={{base: '32px'}}>Get Your</Text>
              </Box>
              <Text variant="header" fontSize={{base: '28px'}} color="white">Subscription now!</Text>
              <Text color="white" mt="5px">Unlock premium features, exclusive perks, and free entries into every giveaway</Text>
            </Box>

            <Box p={{base: '20px', md: '0px'}} m="28px auto 0 auto" maxW="444px" zIndex="9" position="relative">
              <a href={getLinkLoyaltyMembership()}>
                <Button variant="button-lg-g">Subscribe</Button>
              </a>
            </Box>

            <Box position="absolute" bottom="-25px" w="100%"
              left={{base: '-20%', sm: '-10%', lg: '0'}}
              transform={{base: 'scale(0.6)', sm: 'scale(0.8)', lg: 'scale(1)'}}>
              <Image className="animation-account-membership-crystal1" src={crystal} alt="crystal" position="absolute" left="103px" bottom="-10px" transform="rotate(-11deg) scaleX(-1)" width="150px" />
              <Image className="animation-account-membership-crystal2" src={crystal} alt="crystal" position="absolute" left="-115px" bottom="-22px" transform="rotate(18deg) scaleX(-1)" width="340px" />
              <Image className="animation-account-membership-crystal3" src={crystal} alt="crystal" position="absolute" left="-6px" bottom="-25px" transform="rotate(4deg) scaleX(-1)" width="200px" zIndex="8" />
            </Box>

            <Box position="absolute" bottom="-25px" w="100%"
              right={{base: '-20%', sm: '-10%', lg: '0'}}
              transform={{base: 'scale(0.6) scaleX(-1)', sm: 'scale(0.8) scaleX(-1)', lg: 'scale(1) scaleX(-1)'}}>
              <Image className="animation-account-membership-crystal4" src={crystal} alt="crystal" position="absolute" left="103px" bottom="-2px" transform="rotate(11deg) scaleX(-1)" width="140px" />
              <Image className="animation-account-membership-crystal5" src={crystal} alt="crystal" position="absolute" left="-115px" bottom="-12px" transform="rotate(18deg) scaleX(-1)" width="330px" />
              <Image className="animation-account-membership-crystal6" src={crystal} alt="crystal" position="absolute" left="-6px" bottom="-15px" transform="rotate(-40deg) scaleX(-1)" width="200px" zIndex="8" />
            </Box>

          </Box>
        )}
      </Box>

      { userData?.activePlan && userData?.activePlan?.type !== 'fuel' && ( <UpgradeDowngrade userData={userData} />)}
      { userData?.activePlan && userData?.activePlan?.type === 'fuel' && ( <MoreOpportunities userData={userData} />)}

      <Discount />

      { activePlans &&
        <CancelSubscriptionModal
          isOpen={isOpen}
          onClose={onClose}
          onOpen={onOpen} 
          confirmHandleCancelMembership={confirmHandleCancelMembership}
          handleBeforeUnSubscription={handleBeforeUnSubscription}
          subscriptionId={userData?.activeSubscription?.subscriptionId}
          changeStatusSubItem={activePlans[0]}
          userData={userData} />
      }
    </>
  );
};

export default Membership;
